import * as React from "react";
import { type LucideIcon } from "lucide-react";
import { mergeClassNames } from "./class-names";

export interface StatTileProps {
  readonly label: string;
  readonly value: React.ReactNode;
  /** Unit or denominator shown beside the value, e.g. "of 12". */
  readonly valueSuffix?: React.ReactNode;
  readonly icon: LucideIcon;
  /** One line of context under the value — a cap, a breakdown, a timestamp. */
  readonly hint?: React.ReactNode;
  /** Fraction between 0 and 1 drawn as a capacity meter under the value. */
  readonly fill?: number;
  /** `attention` marks a figure the Stable needs to act on. */
  readonly tone?: "default" | "attention";
  readonly className?: string | undefined;
}

/** A single headline operational figure: capacity, roster size, or the count
 * of records waiting on someone. */
export function StatTile({
  label,
  value,
  valueSuffix,
  icon: Icon,
  hint,
  fill,
  tone = "default",
  className,
}: StatTileProps) {
  const clampedFill =
    fill === undefined ? undefined : Math.max(0, Math.min(1, fill));

  return (
    <div
      data-slot="stat-tile"
      data-tone={tone}
      className={mergeClassNames(
        "flex min-w-0 flex-col rounded-xl border border-border-subtle bg-surface p-4",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-body-small font-semibold uppercase text-secondary">
          {label}
        </p>
        <span
          className={mergeClassNames(
            "flex size-8 shrink-0 items-center justify-center rounded-md",
            tone === "attention"
              ? "bg-warning-subtle text-warning"
              : "bg-brand-subtle text-brand-ink",
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
      </div>
      <p className="text-metric-medium mt-3 flex items-baseline gap-1.5 text-primary">
        <span className="tabular-nums">{value}</span>
        {valueSuffix === undefined ? null : (
          <span className="text-body font-semibold text-secondary">
            {valueSuffix}
          </span>
        )}
      </p>
      {clampedFill === undefined ? null : (
        <span
          aria-hidden="true"
          className="mt-3 block h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-sunken)]"
        >
          <span
            className="block h-full rounded-full bg-accent"
            style={{ inlineSize: `${(clampedFill * 100).toFixed(1)}%` }}
          />
        </span>
      )}
      {hint === undefined ? null : (
        <p className="text-body-small mt-2 text-secondary">{hint}</p>
      )}
    </div>
  );
}
